// The proforma document rendered as HTML — the same figures, number and
// date the stored PDF carries and the customer sees on their in-page
// review (portal /proforma/:token). READ-ONLY: it draws a document, it has
// no inputs and no actions that change state.
//
// Reused by the admin All-proformas read-only viewer so the customer
// rendering and the admin rendering can never drift into two different
// documents. Every value comes from accessTokenService.toGuestQuotation's
// shape; the line prices are FROZEN on quotation_lines at quote time, so a
// superseded revision renders exactly what was sent, never today's price.
//
// Shared component — takes `lang` and calls t(id, lang) unbound, same as
// SignatureElement / PasswordInput.

import { t } from '../../i18n/index.js';
import { formatMoney } from '../../money.js';
import { formatQty } from '../../qty.js';
import styles from './ProformaDocument.module.css';

const EAT_TIME_ZONE = 'Africa/Addis_Ababa';

// Same UTC-stored / EAT-displayed formatting the two app-local formatDate
// utils use — inlined here rather than reaching into an app's utils folder
// from shared/.
function formatDate(iso, lang) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat(lang === 'am' ? 'am-ET' : 'en-ET', {
    timeZone: EAT_TIME_ZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d);
}

// Sealed-bid tenders: the scheduled bid-opening time needs hour/minute
// precision, unlike every other date this component shows — same EAT
// display rule, just with a time-of-day. Mirrors the two apps' own
// formatDateTime utils (e.g. portal/src/utils/formatDate.js) for the same
// reasoning formatDate's own comment gives.
function formatDateTime(iso, lang) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat(lang === 'am' ? 'am-ET' : 'en-ET', {
    timeZone: EAT_TIME_ZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

export function ProformaDocument({
  document,
  lang = 'en',
  organizationName,
  refNo,
  statusLabel,
  downloadHref,
  pdfPending = false,
  notIssued = false,
  banner = null,
}) {
  const currency = document.currency || 'ETB';
  const issuedDate = formatDate(document.sent_at, lang);
  const validDate = formatDate(document.valid_until, lang);
  // Editable-terms task: our DELIVERY COMMITMENT, distinct from the buyer's
  // own requested needed-by date (requests.needed_by) — that field belongs
  // to the request/workspace screens, not this document, so there is no
  // confusion risk here; this row is always OUR stated lead time. Phrased
  // identically to jobs/renderQuotationPdf.js's terms-section wording ("...
  // day(s) from confirmed order and payment") per Ermiyas's instruction to
  // keep the existing PDF phrasing, so the on-screen view and the PDF never
  // say two different things about the same commitment. A lead time of 0
  // days is a real, statable commitment ("ships immediately on payment"),
  // so the check is for null/undefined, not falsiness.
  const deliveryLeadTime =
    document.delivery_days !== null && document.delivery_days !== undefined
      ? t('proformaDoc.deliveryLeadTime.value', lang, { days: document.delivery_days })
      : null;
  const paymentTerms =
    typeof document.payment_terms === 'string' && document.payment_terms.trim() !== ''
      ? document.payment_terms.trim()
      : null;

  // Sealed-bid tenders (migration 020): a buyer-facing caller with a still-
  // sealed quotation gets ONLY the skeleton shape — no `lines` at all (see
  // accessTokenService#toGuestQuotation) — so that is the reliable signal to
  // render the sealed explanation instead of the table, never
  // `document.sealed` alone: the admin read-only viewer (forBuyer: false)
  // always gets the full shape, lines included, even while sealed, and must
  // keep rendering normally. Reaching `document.lines.map` on the skeleton
  // shape would otherwise throw.
  const sealedPending = Boolean(document.sealed && !document.unsealed_at && !document.lines);
  const wasSealed = Boolean(document.sealed && document.unsealed_at);
  const openingAt = formatDateTime(document.seal_bid_opening_at, lang);

  const metaRows = [];
  if (organizationName) metaRows.push(['proformaDoc.organization', organizationName, false]);
  if (refNo) metaRows.push(['proformaDoc.request', refNo, true]);
  if (issuedDate) metaRows.push(['proformaDoc.issuedOn', issuedDate, true]);
  if (validDate) metaRows.push(['proformaDoc.validUntil', validDate, true]);
  if (deliveryLeadTime)
    metaRows.push(['proformaDoc.deliveryLeadTime.label', deliveryLeadTime, false]);
  if (paymentTerms) metaRows.push(['proformaDoc.paymentTerms', paymentTerms, false]);

  return (
    <div className={styles.doc}>
      <div className={styles.headRow}>
        <span className={`${styles.number} tabularNums`}>
          {document.proforma_no || t('proformaDoc.notIssued', lang)}
        </span>
        {statusLabel && <span className={styles.status}>{statusLabel}</span>}
      </div>

      {metaRows.length > 0 && (
        <dl className={styles.metaGrid}>
          {metaRows.map(([id, value, numeric]) => (
            <div className={styles.metaRow} key={id}>
              <dt className={styles.metaKey}>{t(id, lang)}</dt>
              <dd className={numeric ? 'tabularNums' : undefined}>{value}</dd>
            </div>
          ))}
        </dl>
      )}

      {notIssued && <p className={styles.infoNote}>{t('proformaDoc.notIssuedNote', lang)}</p>}
      {wasSealed && (
        <p className={styles.sealedPastNote}>{t('proformaDoc.sealed.wasSealedNote', lang)}</p>
      )}
      {banner}

      {sealedPending ? (
        <div className={styles.sealedBlock}>
          <h2 className={styles.sealedTitle}>{t('proformaDoc.sealed.title', lang)}</h2>
          <p className={styles.sealedBody}>
            {openingAt
              ? t('proformaDoc.sealed.bodyScheduled', lang, { openingAt })
              : t('proformaDoc.sealed.bodyUnscheduled', lang)}
          </p>
        </div>
      ) : (
        <>
          <div className={styles.sectionHead}>
            <h2 className={styles.title}>{t('proformaDoc.title', lang)}</h2>
            {downloadHref ? (
              <a className={styles.download} href={downloadHref}>
                {t('proformaDoc.downloadPdf', lang)}
              </a>
            ) : pdfPending ? (
              <span className={styles.downloadPending}>{t('proformaDoc.pdfPending', lang)}</span>
            ) : null}
          </div>

          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('proformaDoc.col.line', lang)}</th>
                  <th>{t('proformaDoc.col.description', lang)}</th>
                  <th className={styles.num}>{t('proformaDoc.col.qty', lang)}</th>
                  <th className={styles.num}>{t('proformaDoc.col.unitPrice', lang)}</th>
                  <th className={styles.num}>{t('proformaDoc.col.lineTotal', lang)}</th>
                </tr>
              </thead>
              <tbody>
                {document.lines.map((line) => (
                  <tr key={line.line_no}>
                    <td className={styles.num}>{line.line_no}</td>
                    <td>{line.description}</td>
                    <td className={styles.num}>
                      {formatQty(line.qty)} {line.unit}
                    </td>
                    <td className={styles.num}>{formatMoney(line.unit_price)}</td>
                    <td className={styles.num}>{formatMoney(line.line_total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4}>{t('proformaDoc.subtotal', lang)}</td>
                  <td className={styles.num}>{formatMoney(document.subtotal)}</td>
                </tr>
                <tr>
                  <td colSpan={4}>
                    {t('proformaDoc.vat', lang)}
                    {document.vat_percent ? ` (${document.vat_percent}%)` : ''}
                  </td>
                  <td className={styles.num}>{formatMoney(document.vat_amount)}</td>
                </tr>
                <tr className={styles.grand}>
                  <td colSpan={4}>{t('proformaDoc.total', lang)}</td>
                  <td className={styles.num}>
                    {formatMoney(document.total)} {currency}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default ProformaDocument;
